/**
 * WizardContext.jsx
 *
 * Tool Finder wizard state: current step, user selections, navigation handlers,
 * progress dots configuration, sessionStorage persistence and URL-param hydration.
 *
 * Ported from Top Hammer ToolFinder v4 Phase 4.7 with MRT-specific changes:
 *   - DRILLING_METHOD step inserted between APPLICATION and EQUIPMENT_CLASS.
 *   - Unified 'replace' entry path (replaceTab: 'rig' | 'rockDrill') vs TH's split.
 *   - sessionStorage hydrate/persist under `mrt-wizard-state` (UX commitment — E8).
 *   - URL param hydration: ?path=...&line=... for deep-link fast-forward.
 *   - DSC entry-path fires `onDscSelect` callback instead of advancing step.
 *
 * Architecture: React Context + useState pattern (matches CompareContext convention).
 *
 * @module contexts/WizardContext
 * @since 2026-05-15 (Group D Sub-Plan 1, Task D1)
 */

import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import {
  STEPS,
  ENTRY_PATHS,
  INITIAL_SELECTIONS,
  SESSION_STORAGE_KEY,
  STEP_SEQUENCES,
} from '@components/wizard/wizardConstants';
import { getEquipmentClassById } from '@data/wizardData';
// SP3.A.3 (2026-05-18): replaced the inline `deriveFromRig` / `deriveFromRockDrill`
// stubs with the real ported matcher service. The TF v4 source is
// `tophammer-toolfinder-v4/src/services/equipmentMatcher.js` (114 lines).
import { deriveFromRig, deriveFromRockDrill } from '@services/equipmentMatcher';

// TODO D6: real progress config (rendered by ProgressDots). Inline minimal version below
// follows the contract documented in the plan: returns { totalSteps, currentIndex, sequence }.
function getProgressConfig(
  currentStep,
  entryPath,
  requiresThreadChoice,
  requiresEquipmentThreadChoice
) {
  if (!entryPath) return { totalSteps: 0, currentIndex: 0, sequence: [] };
  const base = STEP_SEQUENCES[entryPath] || [];
  const seq = [...base];

  if (entryPath === ENTRY_PATHS.EXPLORE && requiresEquipmentThreadChoice) {
    const idx = seq.indexOf(STEPS.EQUIPMENT_CLASS);
    if (idx >= 0) seq.splice(idx + 1, 0, STEPS.EQUIPMENT_THREAD_CHOICE);
  }
  if (entryPath === ENTRY_PATHS.REPLACE && requiresThreadChoice) {
    const idx = seq.indexOf(STEPS.EQUIPMENT_SELECT);
    if (idx >= 0) seq.splice(idx + 1, 0, STEPS.THREAD_CHOICE);
  }

  const currentIndex = seq.indexOf(currentStep);
  return {
    totalSteps: seq.length,
    currentIndex: Math.max(0, currentIndex),
    sequence: seq,
  };
}

// ============================================================================
// SessionStorage helpers — never let storage failures crash the wizard
// ============================================================================

function readPersistedState() {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writePersistedState(state) {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Quota / privacy mode / disabled — silently ignore.
  }
}

// ============================================================================
// URL param hydration — supports ?path=explore|replace|dsc&line=th|dth
// ============================================================================

function parseUrlHydration(search) {
  if (!search || typeof search !== 'string') return null;
  try {
    const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
    const path = params.get('path');
    const line = params.get('line');
    if (!path) return null;
    const normalisedPath = String(path).toLowerCase();
    const allowed = [ENTRY_PATHS.EXPLORE, ENTRY_PATHS.REPLACE, ENTRY_PATHS.DSC];
    if (!allowed.includes(normalisedPath)) return null;
    // Normalise to uppercase to match DRILLING_METHODS.id ('TH' | 'DTH').
    const normalisedLine = line ? String(line).toUpperCase() : null;
    const validLine = normalisedLine === 'TH' || normalisedLine === 'DTH' ? normalisedLine : null;
    return { path: normalisedPath, line: validLine };
  } catch {
    return null;
  }
}

// ============================================================================
// Context
// ============================================================================

const WizardContext = createContext(null);

/**
 * Hook to consume WizardContext. Throws if used outside provider.
 * @returns {Object} Wizard state, selections, handlers, progressConfig.
 */
export function useWizard() {
  const context = useContext(WizardContext);
  if (!context) throw new Error('useWizard must be used within WizardProvider');
  return context;
}

/**
 * WizardProvider — wraps the ToolFinder module with wizard state.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {string}   [props.initialUrlSearch]  - `location.search` from the host page,
 *                                               for fast-forward hydration on mount.
 * @param {Function} [props.onReachResults]    - Called when wizard reaches RESULTS.
 * @param {Function} [props.onDscSelect]       - Called when user picks the DSC card
 *                                               on the entry step (page-level layer
 *                                               navigates to `/dsc`).
 */
export function WizardProvider({
  children,
  initialUrlSearch,
  onReachResults,
  onDscSelect,
}) {
  // --------------------------------------------------------------------------
  // Mount-time hydration: URL params > sessionStorage > defaults
  // --------------------------------------------------------------------------
  const [{ initialStep, initialSelections }] = useState(() => {
    const urlHydration = parseUrlHydration(initialUrlSearch);
    if (urlHydration) {
      const { path, line } = urlHydration;
      // DSC deep-link can't fast-forward inside the wizard — handled by host.
      if (path === ENTRY_PATHS.DSC) {
        return {
          initialStep: STEPS.ENTRY,
          initialSelections: { ...INITIAL_SELECTIONS, entryPath: ENTRY_PATHS.DSC },
        };
      }
      if (path === ENTRY_PATHS.EXPLORE) {
        if (line) {
          // Fast-forward past ENTRY + APPLICATION + DRILLING_METHOD → EQUIPMENT_CLASS.
          return {
            initialStep: STEPS.EQUIPMENT_CLASS,
            initialSelections: {
              ...INITIAL_SELECTIONS,
              entryPath: ENTRY_PATHS.EXPLORE,
              productLine: line,
            },
          };
        }
        return {
          initialStep: STEPS.APPLICATION,
          initialSelections: { ...INITIAL_SELECTIONS, entryPath: ENTRY_PATHS.EXPLORE },
        };
      }
      if (path === ENTRY_PATHS.REPLACE) {
        return {
          initialStep: STEPS.EQUIPMENT_SELECT,
          initialSelections: { ...INITIAL_SELECTIONS, entryPath: ENTRY_PATHS.REPLACE },
        };
      }
    }
    // No URL hydration → try sessionStorage.
    const persisted = readPersistedState();
    if (persisted && persisted.currentStep && persisted.selections) {
      return {
        initialStep: persisted.currentStep,
        initialSelections: { ...INITIAL_SELECTIONS, ...persisted.selections },
      };
    }
    return { initialStep: STEPS.ENTRY, initialSelections: INITIAL_SELECTIONS };
  });

  const [currentStep, setCurrentStep] = useState(initialStep);
  const [selections, setSelections] = useState(initialSelections);

  // --------------------------------------------------------------------------
  // Persist to sessionStorage on every change
  // --------------------------------------------------------------------------
  useEffect(() => {
    writePersistedState({ currentStep, selections });
  }, [currentStep, selections]);

  // --------------------------------------------------------------------------
  // Every step starts at the top of itself.
  //
  // WHY. The browser KEEPS THE SCROLL OFFSET when a step swaps, because the
  // step is a re-render rather than a navigation. On a phone the steps stack
  // into one column, so pressing Continue from the bottom of a long step lands
  // the user part-way down the next one — with its heading and often its first
  // field scrolled off the top. Found in DTH at 375x812, where the first
  // column sat 446px above the viewport, the user filled only what they could
  // see, and every figure downstream came out zero. MyRockTools stacks the
  // same way (grid-cols-1 lg:grid-cols-2) and had no reset at all.
  //
  // ⚠ WHY ONE EFFECT AND NOT 32 CALL SITES. `setCurrentStep` is called from 32
  // places in this file. Adding a scroll to each is 32 chances to miss one, and
  // the easiest to miss are Back and start-over — precisely the paths someone
  // uses when they are already lost. Keying off the value covers every path,
  // including ones nobody has added yet.
  //
  // ⚠ AND WHY IT MUST NOT FIRE ON THE FIRST RUN. This wizard REMEMBERS where
  // you were: the step is restored from sessionStorage above. Vandana,
  // 2026-08-21: "yes we need to remember the steps." A returning user must land
  // where they left off, not be yanked to the top of a step they had already
  // scrolled into. So the first run after mount is skipped and only genuine
  // step CHANGES reset.
  //
  // `instant` deliberately. This is a page swap, not a movement the user asked
  // for. CheckoutWizard uses `smooth` for its own reasons and is not touched.
  // --------------------------------------------------------------------------
  const stepScrollPrimedRef = useRef(false);
  useEffect(() => {
    if (!stepScrollPrimedRef.current) {
      stepScrollPrimedRef.current = true;   // the restored step keeps its offset
      return;
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [currentStep]);

  // --------------------------------------------------------------------------
  // RESULTS callback — fire once per arrival on RESULTS step.
  // --------------------------------------------------------------------------
  const reachedResultsRef = useRef(false);
  useEffect(() => {
    if (currentStep === STEPS.RESULTS) {
      if (!reachedResultsRef.current) {
        reachedResultsRef.current = true;
        if (typeof onReachResults === 'function') onReachResults();
      }
    } else {
      reachedResultsRef.current = false;
    }
  }, [currentStep, onReachResults]);

  // --------------------------------------------------------------------------
  // Progress dots config
  // --------------------------------------------------------------------------
  const progressConfig = useMemo(
    () =>
      getProgressConfig(
        currentStep,
        selections.entryPath,
        selections.requiresThreadChoice,
        selections.requiresEquipmentThreadChoice
      ),
    [
      currentStep,
      selections.entryPath,
      selections.requiresThreadChoice,
      selections.requiresEquipmentThreadChoice,
    ]
  );

  // ============================================================================
  // GENERIC HANDLERS
  // ============================================================================

  const handleSelect = useCallback((field, value, extra = {}) => {
    setSelections(prev => ({ ...prev, [field]: value, ...extra }));
  }, []);

  const canProceed = useCallback(() => {
    switch (currentStep) {
      case STEPS.APPLICATION: return !!selections.application;
      case STEPS.DRILLING_METHOD: return !!selections.productLine;
      case STEPS.EQUIPMENT_CLASS: return !!selections.equipmentClass;
      case STEPS.EQUIPMENT_THREAD_CHOICE:
        // BUGFIX 18 May: DTH writes the sub-step pick to selections.shankType
        // (the parallel of TH's selections.threadSystem). canProceed must
        // check the right field for each productLine, otherwise the Next
        // button stays disabled on the DTH shank-choice step.
        return selections.productLine === 'DTH'
          ? !!selections.shankType
          : !!selections.threadSystem;
      case STEPS.HOLE_DIAMETER: return true;
      case STEPS.ROCK_CONDITIONS: return true;
      default: return true;
    }
  }, [currentStep, selections]);

  // ============================================================================
  // ENTRY POINT (Step 0)
  // MRT: 'replace' unifies legacy rig + rockDrill paths into EQUIPMENT_SELECT.
  // 'dsc' fires onDscSelect (page-level navigation to /dsc) instead of advancing.
  // ============================================================================

  const handleEntryPathSelect = useCallback((path) => {
    setSelections(prev => ({ ...prev, entryPath: path }));
    switch (path) {
      case ENTRY_PATHS.EXPLORE:
        setCurrentStep(STEPS.APPLICATION);
        break;
      case ENTRY_PATHS.REPLACE:
        setCurrentStep(STEPS.EQUIPMENT_SELECT);
        break;
      case ENTRY_PATHS.DSC:
        // DSC is page-level navigation — do NOT advance wizard step.
        if (typeof onDscSelect === 'function') onDscSelect();
        break;
      default:
        setCurrentStep(STEPS.APPLICATION);
    }
  }, [onDscSelect]);

  // ============================================================================
  // PATH A: EXPLORE FLOW
  // MRT: APPLICATION → DRILLING_METHOD → EQUIPMENT_CLASS (DRILLING_METHOD new)
  // ============================================================================

  /**
   * Set selections.productLine ('TH' | 'DTH') from StepDrillingMethod and advance.
   * Uppercase matches DRILLING_METHODS.id and Sandvik item-class codes.
   * @param {'TH' | 'DTH'} productLine
   */
  const handleProductLineSelect = useCallback((productLine) => {
    setSelections(prev => ({ ...prev, productLine }));
    setCurrentStep(STEPS.EQUIPMENT_CLASS);
  }, []);

  const handleNextExplore = useCallback(() => {
    switch (currentStep) {
      case STEPS.APPLICATION:
        setCurrentStep(STEPS.DRILLING_METHOD);
        break;
      case STEPS.DRILLING_METHOD:
        setCurrentStep(STEPS.EQUIPMENT_CLASS);
        break;
      case STEPS.EQUIPMENT_CLASS: {
        const eqClass = getEquipmentClassById(
          selections.equipmentClass,
          selections.productLine,
        );
        // Both TH (threadChoices) and DTH (shankChoices) trigger the
        // conditional sub-step. The picker reads whichever array applies.
        const choices =
          (eqClass && (eqClass.threadChoices || eqClass.shankChoices)) || null;
        if (choices && choices.length > 0) {
          setCurrentStep(STEPS.EQUIPMENT_THREAD_CHOICE);
        } else {
          setCurrentStep(STEPS.HOLE_DIAMETER);
        }
        break;
      }
      case STEPS.EQUIPMENT_THREAD_CHOICE:
        setCurrentStep(STEPS.HOLE_DIAMETER);
        break;
      default:
        break;
    }
  }, [currentStep, selections.equipmentClass, selections.productLine]);

  const handlePreviousExplore = useCallback(() => {
    switch (currentStep) {
      case STEPS.APPLICATION:
        setCurrentStep(STEPS.ENTRY);
        break;
      case STEPS.DRILLING_METHOD:
        setCurrentStep(STEPS.APPLICATION);
        break;
      case STEPS.EQUIPMENT_CLASS:
        setCurrentStep(STEPS.DRILLING_METHOD);
        break;
      case STEPS.EQUIPMENT_THREAD_CHOICE:
        setCurrentStep(STEPS.EQUIPMENT_CLASS);
        break;
      default:
        break;
    }
  }, [currentStep]);  // BUGFIX 17 May: was `[]`, stale-closure froze currentStep at ENTRY so Back did nothing.

  // Equipment thread/shank choice sub-step (A3b / D3d).
  // productLine-aware: TH writes to threadSystem; DTH writes to shankType.
  // We read prev.productLine via the setter so the callback stays stable
  // (no need to add productLine to the dependency array).
  const handleEquipmentThreadChoice = useCallback((value) => {
    setSelections(prev => {
      const field = prev.productLine === 'DTH' ? 'shankType' : 'threadSystem';
      return { ...prev, [field]: value };
    });
  }, []);

  // ============================================================================
  // PATH B/C UNIFIED: EQUIPMENT_SELECT HANDLERS
  // (Inherits TH TF v4 Phase 4.7 shapes — see equipment-select tabs in D3e.)
  // ============================================================================

  /**
   * Handle rig selection (Rig tab).
   * Derives thread system + hole diameter from rig data. Routes to THREAD_CHOICE
   * if multi-thread, else HOLE_DIAMETER. deriveFromRig is stubbed until D7.
   */
  const handleEquipmentSelectRig = useCallback((manufacturer, rig) => {
    const derived = deriveFromRig(rig);
    setSelections(prev => ({
      ...prev,
      rigManufacturer: manufacturer,
      rigModel: rig,
      ...derived,
    }));
    if (derived.requiresThreadChoice) {
      setCurrentStep(STEPS.THREAD_CHOICE);
    } else {
      setCurrentStep(STEPS.HOLE_DIAMETER);
    }
  }, []);

  /**
   * Handle rock-drill selection (Rock Drill tab).
   * Sandvik drills with full data → deriveFromRockDrill.
   * Competitor / unmatched drills → set mfr + model only.
   */
  const handleEquipmentSelectDrill = useCallback((manufacturer, modelName, detailedDrill) => {
    if (detailedDrill) {
      const derived = deriveFromRockDrill(detailedDrill);
      setSelections(prev => ({
        ...prev,
        drillManufacturer: manufacturer,
        drillModelName: modelName,
        rockDrillModel: detailedDrill,
        ...derived,
      }));
    } else {
      setSelections(prev => ({
        ...prev,
        drillManufacturer: manufacturer,
        drillModelName: modelName,
        rockDrillModel: null,
        threadSystem: null,
      }));
    }
    setCurrentStep(STEPS.HOLE_DIAMETER);
  }, []);

  /**
   * "Can't find rig/drill" fallback → switch to Explore path.
   */
  const handleEquipmentSelectFallback = useCallback(() => {
    setSelections(prev => ({ ...prev, entryPath: ENTRY_PATHS.EXPLORE }));
    setCurrentStep(STEPS.APPLICATION);
  }, []);

  // Thread choice for multi-thread rigs after EQUIPMENT_SELECT.
  const handleThreadChoiceSelect = useCallback((thread) => {
    setSelections(prev => ({
      ...prev,
      threadSystem: thread === 'not_sure' ? null : thread,
    }));
    setCurrentStep(STEPS.HOLE_DIAMETER);
  }, []);

  // ============================================================================
  // LEGACY HANDLERS — kept for API compatibility with TH TF v4 Phase 4.7 imports.
  // No current call sites in MRT; do not extend these. Will be removed once
  // every wizard step component has been ported (Sub-Plan 2 onwards).
  // ============================================================================

  const handleRigTypeSelect = useCallback((type) => {
    setSelections(prev => ({ ...prev, rigType: type }));
  }, []);
  const handleRigFamilySelect = useCallback((family) => {
    setSelections(prev => ({ ...prev, rigFamily: family }));
  }, []);
  const handleRigModelSelect = useCallback((rig) => {
    const derived = deriveFromRig(rig);
    setSelections(prev => ({ ...prev, rigModel: rig, ...derived }));
    if (derived.requiresThreadChoice) setCurrentStep(STEPS.THREAD_CHOICE);
    else setCurrentStep(STEPS.HOLE_DIAMETER);
  }, []);
  const handleDrillTypeSelect = useCallback((type) => {
    setSelections(prev => ({ ...prev, rockDrillType: type }));
  }, []);
  const handleDrillFamilySelect = useCallback((family) => {
    setSelections(prev => ({ ...prev, rockDrillFamily: family }));
  }, []);
  const handleDrillModelSelect = useCallback((drill) => {
    const derived = deriveFromRockDrill(drill);
    setSelections(prev => ({ ...prev, rockDrillModel: drill, ...derived }));
    setCurrentStep(STEPS.HOLE_DIAMETER);
  }, []);

  // ============================================================================
  // COMMON STEPS (HOLE_DIAMETER → ROCK_CONDITIONS → SUMMARY → RESULTS)
  // ============================================================================

  const handleHoleDiameterNext = useCallback(() => {
    setCurrentStep(STEPS.ROCK_CONDITIONS);
  }, []);

  const handleHoleDiameterPrevious = useCallback(() => {
    const { entryPath, requiresThreadChoice, requiresEquipmentThreadChoice } = selections;
    if (entryPath === ENTRY_PATHS.REPLACE) {
      setCurrentStep(requiresThreadChoice ? STEPS.THREAD_CHOICE : STEPS.EQUIPMENT_SELECT);
    } else {
      // Explore path
      if (requiresEquipmentThreadChoice) {
        setCurrentStep(STEPS.EQUIPMENT_THREAD_CHOICE);
      } else {
        setCurrentStep(STEPS.EQUIPMENT_CLASS);
      }
    }
  }, [selections]);

  const handleRockConditionsNext = useCallback(() => setCurrentStep(STEPS.SUMMARY), []);
  const handleRockConditionsPrevious = useCallback(() => setCurrentStep(STEPS.HOLE_DIAMETER), []);
  const handleSummaryPrevious = useCallback(() => setCurrentStep(STEPS.ROCK_CONDITIONS), []);
  const handleViewResults = useCallback(() => setCurrentStep(STEPS.RESULTS), []);

  // ============================================================================
  // RESET
  // ============================================================================

  const resetWizard = useCallback(() => {
    setCurrentStep(STEPS.ENTRY);
    setSelections(INITIAL_SELECTIONS);
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
      }
    } catch {
      // ignore
    }
  }, []);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  const value = useMemo(() => ({
    // State
    currentStep,
    setCurrentStep,
    selections,
    setSelections,
    progressConfig,

    // Generic
    handleSelect,
    canProceed,
    resetWizard,

    // Entry
    handleEntryPathSelect,

    // Path A: Explore
    handleProductLineSelect,
    handleNextExplore,
    handlePreviousExplore,
    handleEquipmentThreadChoice,

    // Path B/C unified (Replace)
    handleEquipmentSelectRig,
    handleEquipmentSelectDrill,
    handleEquipmentSelectFallback,
    handleThreadChoiceSelect,

    // Legacy (kept for API compat — see comment block above)
    handleRigTypeSelect,
    handleRigFamilySelect,
    handleRigModelSelect,
    handleDrillTypeSelect,
    handleDrillFamilySelect,
    handleDrillModelSelect,

    // Common
    handleHoleDiameterNext,
    handleHoleDiameterPrevious,
    handleRockConditionsNext,
    handleRockConditionsPrevious,
    handleSummaryPrevious,
    handleViewResults,
  }), [
    currentStep, selections, progressConfig,
    handleSelect, canProceed, resetWizard,
    handleEntryPathSelect,
    handleProductLineSelect, handleNextExplore, handlePreviousExplore, handleEquipmentThreadChoice,
    handleEquipmentSelectRig, handleEquipmentSelectDrill, handleEquipmentSelectFallback, handleThreadChoiceSelect,
    handleDrillTypeSelect, handleDrillFamilySelect, handleDrillModelSelect,
    handleHoleDiameterNext, handleHoleDiameterPrevious,
    handleRockConditionsNext, handleRockConditionsPrevious,
    handleSummaryPrevious, handleViewResults,
  ]);

  return (
    <WizardContext.Provider value={value}>
      {children}
    </WizardContext.Provider>
  );
}

export default WizardContext;
